import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import _ from 'lodash';
import classes from './importProducts.module.scss';
import Api from '../../../../api/api';
import Utils from '../../../../utils/utils';
import Button from '../../../_common/Form/Buttons/Button';
import Modal from '../../../_common/Modal/Modal';
import Select from '../../../_common/Form/Selects/Select/Select';
import Table from '../../../_common/Tables/Table';
import InputField from '../../../_common/Form/TextFields/InputField';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';
import { ReactComponent as ApproveIcon } from '../../../../assets/icons/approve.svg';
import { ReactComponent as DeniedIcon } from '../../../../assets/icons/denied.svg';

const allowTypes = '.xlsx, .xls';

const headerAliases = {
  name: ['itemdescription', 'itemname', 'drugname', 'websiteitemdescription'],
  ndc: ['ndc', 'upc', 'productcode', 'itemnumber'],
  quantity: ['netquantity', 'netqty', 'qty', 'quantity'],
  unitPrice: ['priceperpackage', 'unitprice', 'unitcost'],
  totalPrice: ['extendedprice', 'netsales', 'totalprice', 'totalcost'],
};

const findHeader = (header, aliases) => aliases
  .map((alias) => header.find((h) => h.toLowerCase().replace(/\s/g, '').includes(alias)))
  .find(Boolean) || '';

const syncOptions = (data, path) => {
  const currentValues = [];
  _.forEach(data, (value, key) => {
    if (value && typeof value === 'string' && key !== path) {
      currentValues.push(value);
    }
  });

  return data.header.filter((h) => !currentValues.includes(h));
};

const ImportProducts = () => {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [sendFile, setSendFile] = useState({});
  const [importProductLoading, setImportProductLoading] = useState(false);
  const [invalidRows, setInvalidRows] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);

  const closeModal = () => {
    setOpenModal(false);
    setSendFile({});

    setImportProductLoading(false);
  };

  const clearFile = () => {
    setSendFile({});
  };

  const onUploadFile = async (e) => {
    const XLSX = await import('xlsx');

    Utils.onUploadFile(e, (file) => {
      const reader = new FileReader();

      reader.onload = ({ target }) => {
        try {
          const data = new Uint8Array(target.result);

          const workbook = XLSX.read(data, {
            type: 'array',
            raw: false,
          });

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          const json = XLSX.utils.sheet_to_json(sheet);

          const header = Object.keys(json[0]);

          if (header.length) {
            setSendFile({
              sheet: file,
              header,
              name: findHeader(header, headerAliases.name),
              ndc: findHeader(header, headerAliases.ndc),
              quantity: findHeader(header, headerAliases.quantity),
              unitPrice: findHeader(header, headerAliases.unitPrice),
              price: findHeader(header, headerAliases.totalPrice),
            });
          } else {
            toast.error('Something went wrong, please upload a valid file');
          }
        } catch (err) {
          console.warn(err);
          toast.error('Something went wrong, please upload a valid file');
        }

        e.target.value = '';
      };

      reader.readAsArrayBuffer(file);
    }, allowTypes);
  };

  const changeFileHeader = (path, value) => {
    setSendFile((prev) => ({ ...prev, [path]: value }));
  };

  const importFile = async () => {
    setImportProductLoading(true);

    try {
      const { data } = await Api.productUploadXLSXFile(Utils.deleteEmptyKeys(sendFile));

      toast.success(data.message);

      closeModal();

      setInvalidRows(data.invalidRows.map((r) => ({ ...r, hasError: true })) || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Something went wrong');
    }

    setImportProductLoading(false);
  };

  const deleteInvalidRow = (row) => {
    setInvalidRows((prev) => prev.filter((r) => r !== row));
  };

  const editInvalidRow = (row) => {
    setEditRow({
      row,
      data: {
        name: row.name || '',
        ndc: row.ndc || '',
        quantity: row.quantity ?? '',
        packageSize: row.packageSize ?? '',
        unitPrice: row.unitPrice ?? '',
        totalPrice: row.totalPrice ?? '',
      },
    });
  };

  const closeEditModal = () => {
    setEditRow(null);
    setEditErrors({});

    setSaveLoading(false);
  };

  const changeEditRowData = (path, value) => {
    setEditErrors((prev) => ({ ...prev, [path]: '' }));

    setEditRow((prev) => ({
      ...prev,
      data: { ...prev.data, [path]: value },
    }));
  };

  const saveEditRow = async () => {
    const errors = {};

    const numberFields = ['quantity', 'packageSize', 'unitPrice', 'totalPrice'];

    _.forEach(editRow.data, (value, key) => {
      if (!value.toString().trim()) {
        errors[key] = 'Field is required';
      } else if (numberFields.includes(key) && +value === 0) {
        errors[key] = 'Value must be greater than 0';
      }
    });

    if (!_.isEmpty(errors)) {
      setEditErrors(errors);

      toast.error('Please correct all errors');

      return;
    }

    setSaveLoading(true);

    try {
      await Api.upsertProduct(editRow.data);

      setInvalidRows((prev) => prev.map((r) => (r === editRow.row ? { ...r, ...editRow.data, hasError: false } : r)));

      closeEditModal();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Something went wrong');
    }

    setSaveLoading(false);
  };

  const invalidRowsHeader = [
    {
      name: 'Name',
      path: 'name',
      render: (d) => (
        <div className={classes.name_cell}>
          {d.hasError
            ? <DeniedIcon className={classes.status_icon_invalid} />

            : <ApproveIcon className={classes.status_icon_valid} />}

          <div>
            <p>{d.name}</p>

            {d.hasError && (
              <span className={classes.reason_text}>
                {d.missing?.length ? `Missing: ${d.missing.join(', ')}` : 'Product not found'}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      name: 'NDC',
      path: 'ndc',
    },
    {
      name: 'Quantity',
      path: 'quantity',
    },
    {
      name: 'Package Size',
      path: 'packageSize',
    },
    {
      name: 'Unit Cost',
      path: 'unitPrice',
      render: (d) => (d.unitPrice ? `$${d.unitPrice}` : '-'),
    },
    {
      name: 'Total Cost',
      path: 'totalPrice',
      render: (d) => (d.totalPrice ? `$${d.totalPrice}` : '-'),
    },
    {
      name: '',
      path: 'actions',
      render: (d) => (
        <div className={classes.actions_cell}>
          <button
            type="button"
            className={classes.edit_btn}
            onClick={() => d.hasError && editInvalidRow(d)}
            disabled={!d.hasError}
          >
            <EditIcon />
          </button>

          <button
            type="button"
            className={classes.delete_btn}
            onClick={() => d.hasError && deleteInvalidRow(d)}
            disabled={!d.hasError}
          >
            <DeleteIcon />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h2>Import Products</h2>

        {invalidRows && (
          <div className={classes.header_btn}>
            <Button
              onClick={() => setOpenModal(true)}
              title="Import product"
              type="button"
              className={classes.btn}
              downloadButton
              buttonColor="#5F6C8E"
            />

            <Button
              onClick={() => navigate('/products')}
              title="Cancel"
              type="button"
              clearButton
              className={classes.btn}
            />
          </div>
        )}
      </div>

      {invalidRows
        ? (
          <div className={classes.result_wrapper}>
            <Table
              data={invalidRows}
              header={invalidRowsHeader}
              notFoundText="All products have been successfully imported"
            />
          </div>
        )

        : (
          <div className={classes.import_btn_wrapper}>
            <Button
              onClick={() => setOpenModal(true)}
              title="Import product"
              type="button"
              className={classes.btn_import}
              downloadButton
              buttonColor="#5F6C8E"
            />
          </div>
        )}

      <Modal
        open={openModal}
        onClose={closeModal}
        className={classes.import_modal}
      >
        {sendFile.sheet
          ? (
            <>
              <h2>Header Sync</h2>

              <div className={classes.modal_fields_wrapper}>
                <Select
                  label="Name"
                  options={syncOptions(sendFile, 'name')}
                  currentOption={sendFile.name}
                  onChange={(option) => changeFileHeader('name', option)}
                  zIndex={200}
                />

                <Select
                  label="NDC"
                  options={syncOptions(sendFile, 'ndc')}
                  currentOption={sendFile.ndc}
                  onChange={(option) => changeFileHeader('ndc', option)}
                  zIndex={200}
                />

                <Select
                  label="Quantity"
                  options={syncOptions(sendFile, 'quantity')}
                  currentOption={sendFile.quantity}
                  onChange={(option) => changeFileHeader('quantity', option)}
                  zIndex={200}
                />

                <Select
                  label="Unit Cost"
                  options={syncOptions(sendFile, 'unitPrice')}
                  currentOption={sendFile.unitPrice}
                  onChange={(option) => changeFileHeader('unitPrice', option)}
                  zIndex={200}
                />

                <Select
                  label="Total Cost"
                  options={syncOptions(sendFile, 'price')}
                  currentOption={sendFile.price}
                  onChange={(option) => changeFileHeader('price', option)}
                  zIndex={200}
                />
              </div>

              <div className={classes.modal_btns_wrapper}>
                <Button
                  onClick={clearFile}
                  title="Back"
                  type="button"
                  clearButton
                  className={classes.modal_btn}
                />

                <Button
                  onClick={importFile}
                  title="Import products"
                  type="button"
                  reverseColor
                  className={classes.modal_btn}
                  loading={importProductLoading}
                />
              </div>
            </>
          )

          : (
            <>
              <h2>Import Products</h2>

              <div className={classes.modal_upload_wrapper}>
                <input
                  type="file"
                  id="addXLSX"
                  accept={allowTypes}
                  onChange={onUploadFile}
                />

                <p>
                  Add XLSX file
                </p>
              </div>
            </>
          )}
      </Modal>

      <Modal
        open={!!editRow}
        onClose={closeEditModal}
        className={classes.import_modal}
      >
        <h2>Edit Product</h2>

        <div className={classes.modal_fields_wrapper}>
          <InputField
            label="Name"
            value={editRow?.data.name ?? ''}
            onChange={(value) => changeEditRowData('name', value)}
            error={editErrors.name}
          />

          <InputField
            label="NDC"
            value={editRow?.data.ndc ?? ''}
            onChange={(value) => changeEditRowData('ndc', value)}
            error={editErrors.ndc}
          />

          <InputField
            label="Quantity"
            value={editRow?.data.quantity ?? ''}
            onChange={(value) => changeEditRowData('quantity', value)}
            onKeyPress={(e) => Utils.keyPressCurrency(e, false, undefined, 2)}
            onPaste={(e) => Utils.keyPressCurrency(e, true, undefined, 2)}
            error={editErrors.quantity}
          />

          <InputField
            label="Package Size"
            value={editRow?.data.packageSize ?? ''}
            onChange={(value) => changeEditRowData('packageSize', value)}
            onKeyPress={(e) => Utils.keyPressCurrency(e, false, undefined, 2)}
            onPaste={(e) => Utils.keyPressCurrency(e, true, undefined, 2)}
            error={editErrors.packageSize}
          />

          <InputField
            label="Unit Cost"
            value={editRow?.data.unitPrice ?? ''}
            onChange={(value) => changeEditRowData('unitPrice', value)}
            onKeyPress={(e) => Utils.keyPressCurrency(e, false, undefined, 2)}
            onPaste={(e) => Utils.keyPressCurrency(e, true, undefined, 2)}
            error={editErrors.unitPrice}
          />

          <InputField
            label="Total Cost"
            value={editRow?.data.totalPrice ?? ''}
            onChange={(value) => changeEditRowData('totalPrice', value)}
            onKeyPress={(e) => Utils.keyPressCurrency(e, false, undefined, 2)}
            onPaste={(e) => Utils.keyPressCurrency(e, true, undefined, 2)}
            error={editErrors.totalPrice}
          />
        </div>

        <div className={classes.modal_btns_wrapper}>
          <Button
            onClick={closeEditModal}
            title="Cancel"
            type="button"
            clearButton
            className={classes.modal_btn}
          />

          <Button
            onClick={saveEditRow}
            title="Save"
            type="button"
            reverseColor
            className={classes.modal_btn}
            loading={saveLoading}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ImportProducts;
